// Diagnostics capture for the one-click "Report issue" button.
// ---------------------------------------------------------------------------
// Keeps small ring buffers of console/JS errors and failed API calls so that
// when a user hits "Report issue" we can attach everything needed to solve a
// problem that only reproduces on their machine: browser/PC, console errors,
// and each failed request rebuilt as a runnable curl. No secrets are captured
// (Authorization/token/cookie headers are redacted).

const MAX = 50;
const errorBuffer = [];
const networkBuffer = [];

function push(buf, item) {
  buf.push(item);
  while (buf.length > MAX) buf.shift();
}

function stringifyArg(a) {
  try {
    if (a instanceof Error) return a.stack || a.message || String(a);
    if (typeof a === "object" && a !== null) return JSON.stringify(a);
    return String(a);
  } catch {
    return String(a);
  }
}

export function recordError(level, args) {
  try {
    const message = (Array.isArray(args) ? args : [args]).map(stringifyArg).join(" ");
    push(errorBuffer, { level, message: message.slice(0, 2000), ts: new Date().toISOString() });
  } catch {
    /* never let capture throw */
  }
}

export function recordNetworkFailure(entry) {
  try {
    push(networkBuffer, entry);
  } catch {
    /* ignore */
  }
}

let installed = false;
export function initDiagnostics() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const origErr = console.error ? console.error.bind(console) : () => {};
  console.error = (...a) => {
    recordError("error", a);
    origErr(...a);
  };
  const origWarn = console.warn ? console.warn.bind(console) : () => {};
  console.warn = (...a) => {
    recordError("warn", a);
    origWarn(...a);
  };

  window.addEventListener("error", (e) => {
    if (e?.message) recordError("error", [`${e.message} @ ${e.filename || ""}:${e.lineno || ""}:${e.colno || ""}`]);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    recordError("error", [`UnhandledRejection: ${r?.stack || r?.message || stringifyArg(r)}`]);
  });
}

const SECRET_RE = /authorization|token|cookie|password|secret/i;

// Rebuild an axios request config as a runnable curl command (secrets masked).
export function buildCurl(cfg = {}, fallbackBase = "") {
  try {
    const method = (cfg.method || "get").toUpperCase();
    const url = (cfg.baseURL || fallbackBase || "") + (cfg.url || "");
    let s = `curl -X ${method} '${url}'`;
    const headers = cfg.headers || {};
    for (const k of Object.keys(headers)) {
      if (/^(common|get|post|put|delete|patch|head)$/i.test(k)) continue;
      const v = headers[k];
      if (typeof v !== "string" && typeof v !== "number") continue;
      s += ` -H '${k}: ${SECRET_RE.test(k) ? "<redacted>" : v}'`;
    }
    if (cfg.data != null) {
      const body = typeof cfg.data === "string" ? cfg.data : (() => { try { return JSON.stringify(cfg.data); } catch { return String(cfg.data); } })();
      s += ` --data '${String(body).slice(0, 2000)}'`;
    }
    return s;
  } catch {
    return "";
  }
}

function snippet(v) {
  try {
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return s ? s.slice(0, 1000) : "";
  } catch {
    return "";
  }
}

export function recordAxiosError(error) {
  try {
    const cfg = error?.config || {};
    const response = error?.response;
    recordNetworkFailure({
      method: (cfg.method || "get").toUpperCase(),
      url: (cfg.baseURL || "") + (cfg.url || ""),
      status: response?.status ?? "network error",
      response: response ? snippet(response.data) : (error?.message || ""),
      curl: buildCurl(cfg),
      ts: new Date().toISOString(),
    });
  } catch {
    /* ignore */
  }
}

// Assemble the full diagnostics payload at report time.
export function collectDiagnostics(extra = {}) {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const scr = typeof window !== "undefined" && window.screen ? window.screen : {};
  const env = {
    userAgent: nav.userAgent || "",
    platform: nav.platform || "",
    language: nav.language || "",
    screen: scr.width ? `${scr.width}x${scr.height}` : "",
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    dpr: window.devicePixelRatio || 1,
    timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; } })(),
    online: nav.onLine,
    cookies: nav.cookieEnabled,
    deviceMemory: nav.deviceMemory,
    cores: nav.hardwareConcurrency,
  };

  let auth = null;
  try { auth = JSON.parse(localStorage.getItem("ww_admin_auth") || "null"); } catch { /* ignore */ }
  const user = auth
    ? {
        id: auth.user?.user_id ?? auth.user?.id ?? null,
        username: auth.user?.username ?? null,
        name: [auth.user?.firstname, auth.user?.lastname].filter(Boolean).join(" ") || null,
        group: auth.permissions?.group ?? auth.user?.user_group_id ?? null,
      }
    : null;

  let marketplace = null;
  try { marketplace = localStorage.getItem("ww_active_marketplace"); } catch { /* ignore */ }

  return {
    page: { url: window.location.href, path: window.location.pathname + window.location.search, label: extra.label || document.title },
    env,
    user,
    marketplace,
    maps: { authFailed: !!window.__gmAuthFailed },
    errors: errorBuffer.slice(),
    network: networkBuffer.slice(),
    clientTime: new Date().toISOString(),
    appUrl: window.location.origin,
    ...(extra.extra || {}),
  };
}

// Install immediately on import so capture starts as early as possible.
initDiagnostics();
